import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem('rrm_token');
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
        } catch {
            localStorage.removeItem('rrm_token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        localStorage.setItem('rrm_token', data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const adminLogin = useCallback(async (email, password) => {
        const { data } = await api.post('/auth/admin-login', { email, password });
        localStorage.setItem('rrm_token', data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (username, email, password, join_club) => {
        const { data } = await api.post('/auth/register', { username, email, password, join_club });
        localStorage.setItem('rrm_token', data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('rrm_token');
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, adminLogin, register, logout, refresh }),
        [user, loading, login, adminLogin, register, logout, refresh]
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
